import dayjs from "dayjs"
import { map } from "lodash"

import { CLASS_GROUPS } from "#constants/classOptions"
import {
  ALL_SKILL_LEVEL_LABELS,
  CoreSkillLevel,
  DigitalSkillLevel,
  SculptureSkillLevel,
} from "#constants/skillLevelConstants"
import {
  EVALUATION_STATUS,
  EvaluationConstructor,
  EvaluationForDisplay,
  EvaluationTrait,
} from "#customTypes/composedTypes/evaluationTypes"
import { UserConstructor } from "#customTypes/composedTypes/userTypes"
import { Uuid } from "#customTypes/globalTypes"
import { STANDARD_DATE_TIME_FORMAT } from "#utils/dateHelpers"
import { formatSemester } from "#utils/semesterHelpers"
import { camelToWords } from "#utils/textFormatters"

export class Evaluation {
  id: Uuid
  studentId: Uuid
  classGroup: CLASS_GROUPS
  status: EVALUATION_STATUS
  currentSkill: CoreSkillLevel | DigitalSkillLevel | SculptureSkillLevel
  nextSkill: CoreSkillLevel | DigitalSkillLevel | SculptureSkillLevel
  semester: string
  curriculumId: Uuid
  classId: Uuid

  printing?: {
    status: "done" | "error" | "processing" | "pending"
    error?: string
    url?: string
    filename?: string
  }

  didWell?: string
  needsWork?: string
  secretMessage?: string
  checkboxes: Record<string, boolean>

  assignedTo?: Uuid
  assignedBy?: Uuid
  assignedAt?: string
  createdBy?: Uuid
  createdAt?: string
  savedBy?: Uuid
  savedAt?: string
  submittedBy?: Uuid
  submittedAt?: string
  approvedBy?: Uuid
  approvedAt?: string
  lastUpdatedAt?: string
  lastUpdatedBy?: Uuid
  printedBy?: Uuid
  printedAt?: string

  constructor(props: EvaluationConstructor) {
    this.id = props.id
    this.studentId = props.studentId
    this.curriculumId = props.curriculumId
    this.semester = props.semester
    this.classId = props.classId
    this.classGroup = props.classGroup
    this.status = props.status
    this.currentSkill = props.currentSkill
    this.nextSkill = props.nextSkill

    this.printing = props.printing

    this.checkboxes = props.checkboxes

    this.createdBy = props.createdBy
    this.createdAt = props.createdAt
    this.assignedTo = props.assignedTo
    this.assignedBy = props.assignedBy
    this.assignedAt = props.assignedAt
    this.lastUpdatedAt = props.lastUpdatedAt
    this.lastUpdatedBy = props.lastUpdatedBy
    this.approvedBy = props.approvedBy
    this.approvedAt = props.approvedAt
    this.printedBy = props.printedBy
    this.printedAt = props.printedAt

    this.didWell = props.didWell
    this.needsWork = props.needsWork
    this.secretMessage = props.secretMessage
    this.checkboxes = props.checkboxes
  }

  getEvaluationForDisplay = (
    usersById: Record<Uuid, UserConstructor>,
    traitsById: Record<Uuid, EvaluationTrait>
  ): EvaluationForDisplay => {
    const getUserName = (userId: Uuid) => {
      return usersById[userId]?.name ?? "Unknown"
    }
    const getDate = (date: string | undefined) => {
      return date
        ? dayjs(date).format(STANDARD_DATE_TIME_FORMAT)
        : "unknown date"
    }

    const getTraitName = (traitId: Uuid) => {
      return traitsById[traitId]?.name
    }

    const getTraitCategory = (traitId: Uuid) => {
      return traitsById[traitId]?.category
    }

    const checkboxes = map(this.checkboxes, (checked, traitId) => {
      return {
        name: getTraitName(traitId),
        checked,
        id: traitId,
        category: getTraitCategory(traitId),
      }
    })

    return {
      id: this.id,
      studentId: this.studentId,
      curriculumId: this.curriculumId,
      classId: this.classId,
      semester: formatSemester(this.semester),
      classGroup: camelToWords(this.classGroup) ?? "Unknown",
      status: camelToWords(this.status) ?? "Unknown",

      currentSkill: ALL_SKILL_LEVEL_LABELS[this.currentSkill],
      nextSkill: ALL_SKILL_LEVEL_LABELS[this.nextSkill],
      didWell: this.didWell ?? "",
      needsWork: this.needsWork ?? "",
      secretMessage: this.secretMessage ?? "",
      checkboxes,

      createdBy: this.createdBy
        ? `Created by: ${getUserName(this.createdBy)} on ${getDate(this.createdAt)}`
        : undefined,
      assignedBy: this.assignedBy
        ? `Assigned by: ${getUserName(this.assignedBy)} on ${getDate(this.assignedAt)}`
        : undefined,
      assignedToId: this.assignedTo,
      assignedTo: this.assignedTo
        ? `Assigned to: ${getUserName(this.assignedTo)} on ${getDate(this.assignedAt)}`
        : undefined,
      lastUpdatedBy: this.lastUpdatedBy
        ? `Last updated by: ${getUserName(this.lastUpdatedBy)} on ${getDate(this.lastUpdatedAt)}`
        : undefined,
      approvedBy: this.approvedBy
        ? `Approved by: ${getUserName(this.approvedBy)} on ${getDate(this.approvedAt)}`
        : undefined,
      savedBy: this.savedBy
        ? `Saved by: ${getUserName(this.savedBy)} on ${getDate(this.savedAt)}`
        : undefined,
      submittedBy: this.submittedBy
        ? `Submitted by: ${getUserName(this.submittedBy)} on ${getDate(this.submittedAt)}`
        : undefined,
      printedBy: this.printedBy
        ? `Printed by: ${getUserName(this.printedBy)} on ${getDate(this.printedAt)}`
        : undefined,
    }
  }
}
